import { JSX } from 'react';
import { Add, Delete } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Chip, Grid, IconButton, Typography } from '@mui/material';
import * as Types from 'types/types';

interface ProfileSkillsTabProps {
  skills?: Types.Skill[];
  isMobile: boolean;
  onAddSkill: () => void;
  onRemoveSkill: (index: number) => void;
}

function ProfileSkillsTab({
  skills = [],
  isMobile,
  onAddSkill,
  onRemoveSkill,
}: ProfileSkillsTabProps): JSX.Element {
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          mb: { xs: 2, sm: 3 },
          gap: { xs: 1, sm: 0 },
        }}
      >
        <Typography variant={isMobile ? 'subtitle1' : 'h6'}>Skills & Expertise</Typography>
        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={onAddSkill}
          fullWidth={isMobile}
          size={isMobile ? 'small' : 'medium'}
        >
          Add Skill
        </Button>
      </Box>

      <Grid container spacing={{ xs: 1, sm: 2 }} sx={{ maxWidth: '100%' }}>
        {skills.map((skill, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={`${skill.name}-${index}`}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant={isMobile ? 'subtitle2' : 'h6'}
                      component="div"
                      sx={{ fontSize: { xs: '0.9rem', sm: '1.25rem' }, wordBreak: 'break-word' }}
                    >
                      {skill.name}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ wordBreak: 'break-word', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      {skill.category}
                    </Typography>
                    <Chip
                      size="small"
                      label={skill.level}
                      color="primary"
                      variant="outlined"
                      sx={{
                        mt: 1,
                        fontSize: { xs: '0.65rem', sm: '0.75rem' },
                        height: { xs: 20, sm: 24 },
                      }}
                    />
                    {skill.yearsOfExperience && (
                      <Typography
                        variant="caption"
                        display="block"
                        sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                      >
                        {skill.yearsOfExperience} years experience
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(): void => onRemoveSkill(index)}
                    color="error"
                    sx={{ ml: 1 }}
                  >
                    <Delete sx={{ fontSize: { xs: 16, sm: 20 } }} />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {skills.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          No skills added yet. Click &quot;Add Skill&quot; to get started.
        </Typography>
      )}
    </Box>
  );
}

export { ProfileSkillsTab };
